import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getVeersaPointsActivities,
  getVeersaPointsAdminSubmissionDetail,
  getVeersaPointsAdminSubmissions,
  postVeersaPointsAdminCreateSubmission,
  postVeersaPointsEvidence,
  putVeersaPointsAdminSubmissionStatus,
} from "../../http/requests/veersa-points";
import { getFullName } from "../../common/utils/datatable";
import { getAllUserDetails } from "../../redux/actions";
import { veersaPointsBannerSx, veersaPointsChipSx } from "./constants/veersaPointsFormUi";
import {
  VEERSA_POINTS_EVIDENCE_HINTS,
  VEERSA_POINTS_FRAMEWORK_ACTIVITY_CODES,
  VEERSA_POINTS_SELF_LOG_ACTIVITY_CODES,
  activityChartRowsFromVeersaPointsSubmissions,
} from "./constants/veersaPointsConstants";
import SubmissionEvidenceLinks from "./SubmissionEvidenceLinks";
import PointsDashboard from "./PointsDashboard";

const STATUS_FILTER_OPTIONS = ["", "Submitted", "InReview", "Approved", "Rejected"];

const ADMIN_STATUS_OPTIONS = ["InReview", "Approved", "Rejected"];

const MONTH_OPTIONS = [
  { value: "", label: "All months" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function statusDonutSegmentsFromRows(submissionRows) {
  const defs = [
    { key: "Approved", label: "Approved", color: "#639922" },
    { key: "InReview", label: "In review", color: "#EF9F27" },
    { key: "Submitted", label: "Submitted", color: "#378ADD" },
    { key: "Rejected", label: "Rejected", color: "#E24B4A" },
  ];
  const counts = { Approved: 0, InReview: 0, Submitted: 0, Rejected: 0 };
  (submissionRows || []).forEach((r) => {
    const st = (r.status || "").trim();
    if (!st) return;
    if (st === "InReview" || st.toLowerCase() === "inreview") counts.InReview += 1;
    else if (Object.prototype.hasOwnProperty.call(counts, st)) counts[st] += 1;
  });
  return defs.map((d) => ({ ...d, count: counts[d.key] || 0 }));
}

function formatUtcLabel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch {
    return "—";
  }
}

/** Stacked label/value — avoids viewport-wide `sm` breakpoints stretching rows inside a narrow modal. */
function DetailRow({ label, children }) {
  return (
    <Box sx={{ py: 0.35, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4, mb: 0.125 }}>
        {label}
      </Typography>
      <Box sx={{ typography: "body2", lineHeight: 1.5, wordBreak: "break-word", color: "text.primary" }}>{children}</Box>
    </Box>
  );
}

function employeeAzureKey(u) {
  if (!u) return "";
  return u.azureId || u.azureID || u.AzureID || u.id || "";
}

function filterEmployeesBySearch(employees, searchTerm) {
  const list = employees;
  if (!Array.isArray(list)) return [];
  if (!searchTerm) return list;
  const searchLower = searchTerm.toLowerCase();
  return list.filter((employee) => {
    const name = (employee.name || getFullName(employee.firstName, employee.lastName) || "").toLowerCase();
    const email = (employee.email || "").toLowerCase();
    const employeeId = (employee.employeeId || "").toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower) || employeeId.includes(searchLower);
  });
}

/** Same searchable employee field as the dashboard filter (View Attendance–style options). */
function EmployeeAutocompleteSmall({
  employees,
  value,
  onChange,
  searchTerm,
  onSearchTermChange,
  width = 200,
  layoutFullWidth = false,
}) {
  const filtered = useMemo(() => filterEmployeesBySearch(employees, searchTerm), [employees, searchTerm]);
  const tfSx = layoutFullWidth
    ? {
        width: "100%",
        "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8125rem" },
        "& .MuiInputBase-root": { minHeight: 32, height: 32 },
      }
    : {
        width,
        "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8125rem" },
        "& .MuiInputBase-root": { minHeight: 32, height: 32 },
      };

  return (
    <Autocomplete
      size="small"
      options={filtered}
      getOptionLabel={(option) => {
        if (!option) return "";
        const name = option.name || getFullName(option.firstName, option.lastName);
        const employeeId = option.employeeId || "";
        const email = option.email || "";
        return employeeId ? `${name} (${employeeId}) - ${email}` : `${name} - ${email}`;
      }}
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input") onSearchTermChange(newInputValue);
        else if (reason === "clear") onSearchTermChange("");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search employee…"
          variant="outlined"
          sx={tfSx}
        />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ fontWeight: 500, fontSize: "0.8125rem" }}>
              {option.name || getFullName(option.firstName, option.lastName)}
              {option.employeeId ? (
                <Box component="span" sx={{ color: "#666", fontWeight: 400 }}>
                  {" "}
                  ({option.employeeId})
                </Box>
              ) : null}
            </Box>
            <Box sx={{ fontSize: "0.75rem", color: "#666" }}>{option.email}</Box>
          </Box>
        </li>
      )}
      ListboxProps={{ sx: { maxHeight: 160, fontSize: "0.8125rem" } }}
      noOptionsText={
        searchTerm ? "No employees found matching your search" : "Start typing to search employees"
      }
      isOptionEqualToValue={(option, val) => {
        const optionId = option?.azureId || option?.id;
        const valueId = val?.azureId || val?.id;
        return optionId === valueId;
      }}
      freeSolo={false}
      clearOnEscape
      openOnFocus
      sx={layoutFullWidth ? { width: "100%" } : { width, flexShrink: 0 }}
    />
  );
}

/** Admin Points Dashboard — VeersaPointsSubmission grid with filters and status updates. */
export default function VeersaPointsAdminDashboard() {
  const dispatch = useDispatch();
  const { allUserDetails } = useSelector(({ USER }) => USER);
  const now = useMemo(() => new Date(), []);
  const [calendarYear, setCalendarYear] = useState(now.getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(String(now.getMonth() + 1));
  const [employeeAzureId, setEmployeeAzureId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [activityTypeFilter, setActivityTypeFilter] = useState("");
  const [filterActivities, setFilterActivities] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [editStatus, setEditStatus] = useState("InReview");
  const [editComment, setEditComment] = useState("");
  const [saving, setSaving] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addSelectedEmployee, setAddSelectedEmployee] = useState(null);
  const [addActivityId, setAddActivityId] = useState("");
  const [addPointsYear, setAddPointsYear] = useState(now.getFullYear());
  const [addTitle, setAddTitle] = useState("");
  const [addDescription, setAddDescription] = useState("");
  const [addContentLink, setAddContentLink] = useState("");
  const [addEvidenceFile, setAddEvidenceFile] = useState(null);
  const [addAttachment, setAddAttachment] = useState(null);
  const [addSaving, setAddSaving] = useState(false);
  const [activitiesForAdd, setActivitiesForAdd] = useState([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPayload, setDetailPayload] = useState(null);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return Array.from({ length: 9 }, (_, i) => y - 4 + i);
  }, [now]);

  useEffect(() => {
    if (allUserDetails == null) {
      dispatch(getAllUserDetails());
    }
  }, [allUserDetails, dispatch]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rowsAct = await getVeersaPointsActivities();
        if (cancelled || !Array.isArray(rowsAct)) return;
        const mapped = rowsAct
          .filter(
            (r) =>
              r.isActive !== false &&
              VEERSA_POINTS_FRAMEWORK_ACTIVITY_CODES.has(String(r.activityCode || "").toUpperCase())
          )
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setFilterActivities(mapped);
      } catch {
        if (!cancelled) setFilterActivities([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activityTypeFilterOptions = useMemo(
    () => [
      { value: "", label: "All activity types" },
      ...filterActivities.map((a) => ({
        value: String(a.activityId),
        label: a.displayName || `Activity ${a.activityId}`,
      })),
    ],
    [filterActivities]
  );

  const loadGrid = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVeersaPointsAdminSubmissions({
        year: calendarYear,
        month: calendarMonth === "" ? undefined : Number(calendarMonth),
        employeeAzureId: employeeAzureId || undefined,
        status: statusFilter || undefined,
        activityId: activityTypeFilter ? Number(activityTypeFilter) : undefined,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg =
        err?.data?.errorMessage ||
        err?.data?.ErrorMessage ||
        err?.statusText ||
        "Failed to load submissions.";
      toast.error(typeof msg === "string" ? msg : "Failed to load submissions.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [calendarYear, calendarMonth, employeeAzureId, statusFilter, activityTypeFilter]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  useEffect(() => {
    if (!addOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await getVeersaPointsActivities();
        if (cancelled) return;
        const mapped = Array.isArray(rows)
          ? rows
              .filter(
                (r) =>
                  r.isActive !== false &&
                  VEERSA_POINTS_SELF_LOG_ACTIVITY_CODES.has(String(r.activityCode || "").toUpperCase())
              )
              .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          : [];
        setActivitiesForAdd(mapped);
      } catch {
        if (!cancelled) {
          setActivitiesForAdd([]);
          toast.error("Could not load activities.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addOpen]);

  const selectedAddActivity = useMemo(
    () => activitiesForAdd.find((a) => String(a.activityId) === String(addActivityId)),
    [activitiesForAdd, addActivityId]
  );

  const selectedAddActivityCode = String(selectedAddActivity?.activityCode || "").toUpperCase();

  const canSubmitAdd = useMemo(() => {
    if (!addSelectedEmployee || !addActivityId || !addDescription.trim()) return false;
    const hasUploadedEvidence = Boolean(addAttachment?.attachmentUrl || addAttachment?.AttachmentUrl);
    const hasPendingFile = Boolean(addEvidenceFile);
    if (selectedAddActivityCode === "CONTENT_CREATION") {
      return hasUploadedEvidence || hasPendingFile || Boolean(addContentLink.trim());
    }
    if (selectedAddActivityCode === "CERTIFICATION" || selectedAddActivityCode === "MENTORING") {
      return hasUploadedEvidence || hasPendingFile;
    }
    return true;
  }, [
    addSelectedEmployee,
    addActivityId,
    addDescription,
    selectedAddActivityCode,
    addAttachment,
    addEvidenceFile,
    addContentLink,
  ]);

  const resetAddEvidenceFields = () => {
    setAddContentLink("");
    setAddEvidenceFile(null);
    setAddAttachment(null);
  };

  const openAddActivity = () => {
    setAddSelectedEmployee(selectedEmployee || null);
    setAddSearch("");
    setAddActivityId("");
    setAddPointsYear(calendarYear);
    setAddTitle("");
    setAddDescription("");
    resetAddEvidenceFields();
    setAddOpen(true);
  };

  const closeAddActivity = () => {
    setAddOpen(false);
    setAddSearch("");
    setAddSelectedEmployee(null);
    setAddActivityId("");
    setAddPointsYear(now.getFullYear());
    setAddTitle("");
    setAddDescription("");
    resetAddEvidenceFields();
    setAddSaving(false);
  };

  const submitAddActivity = async () => {
    const azure = employeeAzureKey(addSelectedEmployee);
    if (!azure) {
      toast.error("Please select an employee.");
      return;
    }
    if (!addActivityId) {
      toast.error("Please select an activity type.");
      return;
    }
    if (!addPointsYear || addPointsYear < 2000 || addPointsYear > 2100) {
      toast.error("Please select a valid points year.");
      return;
    }
    if (!addDescription.trim()) {
      toast.error("Notes are required.");
      return;
    }
    if (!canSubmitAdd) {
      if (selectedAddActivityCode === "CONTENT_CREATION") {
        toast.error("Upload final content or provide a link for Content Creation.");
      } else if (selectedAddActivityCode === "CERTIFICATION" || selectedAddActivityCode === "MENTORING") {
        toast.error("Supporting evidence upload is required for this activity.");
      }
      return;
    }
    setAddSaving(true);
    try {
      let attachment = addAttachment;
      if (addEvidenceFile && !attachment?.attachmentUrl && !attachment?.AttachmentUrl) {
        const fd = new FormData();
        fd.append("file", addEvidenceFile);
        attachment = await postVeersaPointsEvidence(fd);
        setAddAttachment(attachment);
      }

      const res = await postVeersaPointsAdminCreateSubmission({
        employeeAzureId: azure,
        activityId: Number(addActivityId),
        pointsYear: Number(addPointsYear),
        title: addTitle.trim() || undefined,
        description: addDescription.trim(),
        attachmentUrl: attachment?.attachmentUrl || attachment?.AttachmentUrl,
        attachmentFileName: attachment?.attachmentFileName || attachment?.AttachmentFileName,
        contentLink: addContentLink.trim() || undefined,
      });
      toast.success(res?.message || "Activity added for employee.");
      closeAddActivity();
      await loadGrid();
    } catch (err) {
      const msg =
        err?.data?.errorMessage ||
        err?.data?.ErrorMessage ||
        err?.data?.message ||
        err?.statusText ||
        "Could not create submission.";
      toast.error(typeof msg === "string" ? msg : "Could not create submission.");
    } finally {
      setAddSaving(false);
    }
  };

  const openDetail = async (row) => {
    const id = row?.submissionId;
    if (id == null) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailPayload(null);
    try {
      const data = await getVeersaPointsAdminSubmissionDetail(id);
      if (data && typeof data === "object") {
        const submission = data.submission ?? data.Submission ?? null;
        const history = data.history ?? data.History ?? [];
        if (!submission) {
          toast.error("Submission details were empty.");
          setDetailOpen(false);
          setDetailPayload(null);
          return;
        }
        setDetailPayload({ submission, history: Array.isArray(history) ? history : [] });
      } else {
        setDetailPayload(null);
      }
    } catch (err) {
      const msg =
        err?.data?.errorMessage ||
        err?.data?.ErrorMessage ||
        err?.statusText ||
        "Could not load submission details.";
      toast.error(typeof msg === "string" ? msg : "Could not load submission details.");
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailPayload(null);
    setDetailLoading(false);
  };

  const openEdit = (row) => {
    setActiveRow(row);
    const current = (row.status || "").trim();
    setEditStatus(ADMIN_STATUS_OPTIONS.includes(current) ? current : "InReview");
    setEditComment(row.reviewComment || "");
    setDialogOpen(true);
  };

  const closeEdit = () => {
    setDialogOpen(false);
    setActiveRow(null);
    setEditComment("");
    setSaving(false);
  };

  const saveStatus = async () => {
    if (!activeRow) return;
    setSaving(true);
    try {
      await putVeersaPointsAdminSubmissionStatus(activeRow.submissionId, {
        status: editStatus,
        comment: editComment.trim() || undefined,
      });
      toast.success("Status updated.");
      closeEdit();
      await loadGrid();
    } catch (err) {
      const msg =
        err?.data?.errorMessage ||
        err?.data?.ErrorMessage ||
        err?.statusText ||
        "Update failed.";
      toast.error(typeof msg === "string" ? msg : "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const totalEmployees = Array.isArray(allUserDetails?.data) ? allUserDetails.data.length : 0;

  const kpi = useMemo(() => {
    const total = rows.length;
    let pending = 0;
    let approved = 0;
    const emp = new Set();
    rows.forEach((r) => {
      const st = (r.status || "").trim();
      if (st === "InReview" || st.toLowerCase() === "inreview") pending += 1;
      if (st === "Approved") approved += 1;
      const id = r.employeeAzureId || r.employeeDisplayName || "";
      if (id) emp.add(id);
    });
    return {
      totalSubmissions: total,
      pendingReview: pending,
      approved,
      activeEmployees: emp.size,
    };
  }, [rows]);

  const activityChartRows = useMemo(() => activityChartRowsFromVeersaPointsSubmissions(rows), [rows]);
  const statusDonutSegments = useMemo(() => statusDonutSegmentsFromRows(rows), [rows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <PointsDashboard
        totalSubmissions={kpi.totalSubmissions}
        pendingReview={kpi.pendingReview}
        approved={kpi.approved}
        activeEmployees={kpi.activeEmployees}
        totalEmployees={totalEmployees}
        activityChartRows={activityChartRows}
        statusDonutSegments={statusDonutSegments}
        rows={rows}
        loading={loading}
        onUpdateStatus={(row) => openEdit(row)}
        onAddActivity={openAddActivity}
        onRefresh={loadGrid}
        onRowClick={(row) => openDetail(row)}
        calendarYear={calendarYear}
        yearOptions={yearOptions}
        onYearChange={setCalendarYear}
        calendarMonth={calendarMonth}
        monthOptions={MONTH_OPTIONS}
        onMonthChange={setCalendarMonth}
        statusFilter={statusFilter}
        statusFilterOptions={STATUS_FILTER_OPTIONS}
        onStatusFilterChange={setStatusFilter}
        activityTypeFilter={activityTypeFilter}
        activityTypeFilterOptions={activityTypeFilterOptions}
        onActivityTypeFilterChange={setActivityTypeFilter}
        employeeFilterSlot={
          <EmployeeAutocompleteSmall
            employees={allUserDetails?.data}
            value={selectedEmployee}
            onChange={(newValue) => {
              setSelectedEmployee(newValue);
              setEmployeeAzureId(newValue ? employeeAzureKey(newValue) : "");
            }}
            searchTerm={employeeSearchTerm}
            onSearchTermChange={setEmployeeSearchTerm}
            layoutFullWidth
          />
        }
      />

      <Dialog
        open={detailOpen}
        onClose={detailLoading ? undefined : closeDetail}
        maxWidth={false}
        fullWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            width: "min(44rem, calc(100vw - 2rem))",
            maxWidth: "min(44rem, calc(100vw - 2rem))",
            margin: 2,
            boxSizing: "border-box",
            alignSelf: "center",
          },
        }}
        PaperProps={{
          sx: {
            width: "min(44rem, calc(100vw - 2rem))",
            maxWidth: "min(44rem, calc(100vw - 2rem))",
            boxSizing: "border-box",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Submission details</DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5, maxHeight: "70vh", px: 2.5, minWidth: 0 }}>
          {detailLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!detailLoading && detailPayload?.submission && (
            <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
              <Box sx={{ width: "100%", minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Record
                </Typography>
                <Box sx={{ width: "100%", minWidth: 0 }}>
                  <DetailRow label="Submission ID">{detailPayload.submission.submissionId}</DetailRow>
                  <DetailRow label="Employee">
                    {detailPayload.submission.employeeDisplayName || detailPayload.submission.employeeAzureId || "—"}
                  </DetailRow>
                  <DetailRow label="Activity">{detailPayload.submission.displayName || "—"}</DetailRow>
                  <DetailRow label="Points year">{detailPayload.submission.pointsYear}</DetailRow>
                  <DetailRow label="Status">{detailPayload.submission.status || "—"}</DetailRow>
                  <DetailRow label="Requested / approved pts">
                    {detailPayload.submission.requestedPoints ?? "—"}
                    {detailPayload.submission.approvedPoints != null ? ` → ${detailPayload.submission.approvedPoints}` : ""}
                  </DetailRow>
                  <DetailRow label="Submitted / occurred">
                    {formatUtcLabel(detailPayload.submission.submittedUtc || detailPayload.submission.createdUtc)}
                  </DetailRow>
                  <DetailRow label="Created (UTC)">{formatUtcLabel(detailPayload.submission.createdUtc)}</DetailRow>
                  <DetailRow label="Last modified (UTC)">{formatUtcLabel(detailPayload.submission.modifiedUtc)}</DetailRow>
                  <DetailRow label="Reviewed by">
                    {detailPayload.submission.reviewedByDisplayName ||
                      detailPayload.submission.reviewedByAzureId ||
                      "—"}
                  </DetailRow>
                  <DetailRow label="Reviewed (UTC)">{formatUtcLabel(detailPayload.submission.reviewedUtc)}</DetailRow>
                  <DetailRow label="Title">
                    <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                      {detailPayload.submission.title?.trim() ? detailPayload.submission.title : "—"}
                    </Box>
                  </DetailRow>
                  <DetailRow label="Description">
                    <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                      {detailPayload.submission.description?.trim() ? detailPayload.submission.description : "—"}
                    </Box>
                  </DetailRow>
                  <DetailRow label="Supporting evidence">
                    <SubmissionEvidenceLinks submission={detailPayload.submission} admin />
                  </DetailRow>
                  <DetailRow label="Review comment">
                    <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                      {detailPayload.submission.reviewComment?.trim() ? detailPayload.submission.reviewComment : "—"}
                    </Box>
                  </DetailRow>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ width: "100%", minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Workflow history
                </Typography>
                {Array.isArray(detailPayload.history) && detailPayload.history.length > 0 ? (
                  <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
                    {detailPayload.history.map((h, historyIndex) => {
                      const actor =
                        (h.actorDisplayName && String(h.actorDisplayName).trim()) ||
                        h.ActorDisplayName ||
                        h.actorAzureId ||
                        h.ActorAzureId ||
                        "—";
                      const fromStatus = h.fromStatus ?? h.FromStatus;
                      const toStatus = h.toStatus ?? h.ToStatus;
                      const fromTo =
                        fromStatus != null && String(fromStatus).trim()
                          ? `${fromStatus} → ${toStatus}`
                          : `→ ${toStatus}`;
                      const hid = h.historyId ?? h.HistoryId ?? historyIndex;
                      const comment = h.comment ?? h.Comment;
                      const created = h.createdUtc ?? h.CreatedUtc;
                      return (
                        <Paper
                          key={hid}
                          variant="outlined"
                          sx={{
                            width: "100%",
                            boxSizing: "border-box",
                            p: 1.25,
                            bgcolor: "grey.50",
                            borderLeftWidth: 3,
                            borderLeftStyle: "solid",
                            borderLeftColor: "#3C3489",
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fromTo}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                            {formatUtcLabel(created)} · {actor}
                          </Typography>
                          {comment ? (
                            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                              {comment}
                            </Typography>
                          ) : null}
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No history rows recorded for this submission (older records may predate audit logging).
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, "& .MuiButton-contained": { bgcolor: "#3C3489" } }}>
          <Button onClick={closeDetail} disabled={detailLoading} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Update submission status</DialogTitle>
        <DialogContent>
          {activeRow && (
            <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Submission #{activeRow.submissionId} · {activeRow.displayName}
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="vp-ad-edit-st">New status</InputLabel>
                <Select labelId="vp-ad-edit-st" label="New status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                  {ADMIN_STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Comment (optional)"
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ "& .MuiButton-contained": { bgcolor: "#3C3489" } }}>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveStatus} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addOpen}
        onClose={addSaving ? undefined : closeAddActivity}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            width: "100%",
            maxWidth: {
              xs: "calc(100vw - 2rem)",
              sm: "min(26rem, calc(100vw - 2rem))",
              lg: "min(28rem, calc(100vw - 22rem))",
            },
            m: { xs: 2, sm: 2 },
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
          "@media (min-width: 1024px)": {
            "& .MuiDialog-container": {
              justifyContent: "flex-start",
              paddingLeft: "20.5rem",
              paddingRight: "1.875rem",
            },
          },
        }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
            <StarsRoundedIcon sx={{ color: "#5b21b6", fontSize: 28 }} aria-hidden />
            <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
              Add activity for employee
            </Typography>
            <Chip label="Veersa Points" size="small" sx={{ ...veersaPointsChipSx, height: 26 }} />
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={veersaPointsBannerSx}>
            <StarsRoundedIcon sx={{ color: "#6d28d9", mt: 0.25 }} fontSize="small" aria-hidden />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#4c1d95", mb: 0.5 }}>
                Admin submission
              </Typography>
              <Typography variant="body2" sx={{ color: "#5b21b6", lineHeight: 1.5 }}>
                Select the employee and activity. The submission is created in <strong>Submitted</strong> status and
                follows the same annual caps as self-service logging.
              </Typography>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Employee *
          </Typography>
          <EmployeeAutocompleteSmall
            employees={allUserDetails?.data}
            value={addSelectedEmployee}
            onChange={setAddSelectedEmployee}
            searchTerm={addSearch}
            onSearchTermChange={setAddSearch}
            layoutFullWidth
          />

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="vp-ad-add-year">Points year</InputLabel>
            <Select
              labelId="vp-ad-add-year"
              label="Points year"
              value={addPointsYear}
              onChange={(e) => setAddPointsYear(Number(e.target.value))}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense" size="small">
            <InputLabel id="vp-ad-add-act">Activity type</InputLabel>
            <Select
              labelId="vp-ad-add-act"
              label="Activity type"
              value={addActivityId}
              onChange={(e) => setAddActivityId(e.target.value)}
              renderValue={(id) => {
                const a = activitiesForAdd.find((x) => String(x.activityId) === String(id));
                if (!a) return "";
                return (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%", minWidth: 0 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.displayName}
                    </Typography>
                    <Chip label={`+${a.pointsPerOccurrence} pts`} size="small" sx={veersaPointsChipSx} />
                  </Stack>
                );
              }}
            >
              {activitiesForAdd.map((a) => {
                const pts = Number(a.pointsPerOccurrence);
                const annualCap = pts * a.maxOccurrencesPerYear;
                return (
                  <MenuItem key={a.activityId} value={String(a.activityId)} sx={{ alignItems: "flex-start", py: 1.25, gap: 1 }}>
                    <StarsRoundedIcon sx={{ color: "#7c3aed", mt: 0.35, flexShrink: 0 }} fontSize="small" aria-hidden />
                    <ListItemText
                      primary={a.displayName}
                      secondary={`Up to ${annualCap} pts/yr · max ${a.maxOccurrencesPerYear} occurrence${a.maxOccurrencesPerYear === 1 ? "" : "s"}`}
                      primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: "caption", sx: { color: "#6b7280" } }}
                      sx={{ flex: 1, minWidth: 0, my: 0 }}
                    />
                    <Chip label={`+${pts}`} size="small" sx={veersaPointsChipSx} />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedAddActivity && (
            <Box
              sx={{
                mt: 1.5,
                mb: 0.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#faf5ff",
                border: "2px solid #a78bfa",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#4c1d95" }}>
                Selected activity
              </Typography>
              <Chip
                label={`+${Number(selectedAddActivity.pointsPerOccurrence)} Veersa Points per submission`}
                sx={{ ...veersaPointsChipSx, fontSize: "0.8125rem", height: 30 }}
              />
            </Box>
          )}

          <TextField
            margin="normal"
            fullWidth
            size="small"
            label="Title (optional)"
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Notes"
            required
            helperText="Required for L&D review."
            value={addDescription}
            onChange={(e) => setAddDescription(e.target.value)}
          />

          {selectedAddActivityCode && VEERSA_POINTS_EVIDENCE_HINTS[selectedAddActivityCode] ? (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "#5b21b6" }}>
              {VEERSA_POINTS_EVIDENCE_HINTS[selectedAddActivityCode]}
            </Typography>
          ) : null}

          {(selectedAddActivityCode === "CERTIFICATION" ||
            selectedAddActivityCode === "MENTORING" ||
            selectedAddActivityCode === "CONTENT_CREATION") && (
            <Box sx={{ mt: 1.5 }}>
              <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none" }}>
                Upload supporting evidence
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setAddEvidenceFile(f);
                    setAddAttachment(null);
                  }}
                />
              </Button>
              {(addEvidenceFile || addAttachment?.attachmentFileName || addAttachment?.AttachmentFileName) && (
                <Typography variant="body2" sx={{ mt: 1, color: "#374151" }}>
                  {addEvidenceFile?.name ||
                    addAttachment?.attachmentFileName ||
                    addAttachment?.AttachmentFileName}
                </Typography>
              )}
            </Box>
          )}

          {selectedAddActivityCode === "CONTENT_CREATION" && (
            <TextField
              margin="normal"
              fullWidth
              size="small"
              label="Content link (optional if file uploaded)"
              placeholder="https://..."
              value={addContentLink}
              onChange={(e) => setAddContentLink(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ "& .MuiButton-contained": { bgcolor: "#3C3489" } }}>
          <Button onClick={closeAddActivity} disabled={addSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitAddActivity} disabled={addSaving || !canSubmitAdd}>
            {addSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
